import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import myAxios from "../AxiosUrl";
import styles from "./TournamentSchedule.module.css";
import Loading from "../Loading/Loading";

const TournamentSchedule = () => {
  const [allMatches, setAllMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCompetition, setSelectedCompetition] = useState("");
  const [selectedTournament, setSelectedTournament] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [expandedVenues, setExpandedVenues] = useState(new Set());

  // Get Turkish time (UTC+3)
  const getTurkishTime = () => {
    const now = new Date();
    // Turkish timezone is UTC+3
    const turkishOffset = 3 * 60; // 3 hours in minutes
    const localOffset = now.getTimezoneOffset(); // Local timezone offset in minutes
    const totalOffset = turkishOffset + localOffset;
    return new Date(now.getTime() + (totalOffset * 60 * 1000));
  };

  const itemsPerPage = 15;

  useEffect(() => {
    fetchData();
    setCurrentTime(getTurkishTime());

    // Update current time every second for live countdown
    const timer = setInterval(() => {
      setCurrentTime(getTurkishTime());
    }, 1000); // Update every second

    return () => clearInterval(timer);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await myAxios.get("/tournament-schedule");

      if (response.data.success && response.data.data) {
        const matchData = response.data.data.tournamentSchedule || [];
        setAllMatches(matchData);
      }
    } catch (err) {
      console.log("Error loading data:", err);
      setError("Error fetching tournament schedule");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get match date time - assumes data from backend is in Turkish timezone
  const getMatchDateTime = (match) => {
    try {
      // Prioritize localDate and localTime as they are already in Turkish timezone
      if (match.localDate && match.localTime) {
        // localDate is format "2025-09-17" and localTime is "20:00:00"
        // Simply combine them without timezone offset (treat as local time)
        const dateTimeStr = `${match.localDate}T${match.localTime}`;
        const date = new Date(dateTimeStr);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }

      // Fallback to other date fields
      if (match.localDate) {
        // Use localDate with default time if no localTime
        const timeStr = match.localTime || match.time || "20:00:00";
        const dateTimeStr = `${match.localDate}T${timeStr}`;
        const date = new Date(dateTimeStr);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }

      // Try date field
      if (match.date) {
        let dateStr = match.date.replace("Z", "");
        const timeStr = match.time?.replace("Z", "") || "20:00:00";
        const dateTimeStr = `${dateStr}T${timeStr}`;
        const date = new Date(dateTimeStr);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }

      // Try matchDate field
      if (match.matchDate) {
        const dateStr = match.matchDate.replace("Z", "");
        if (dateStr.length === 10) { // Format: YYYY-MM-DD
          const timeStr = match.time?.replace("Z", "") || match.localTime || "20:00:00";
          const dateTimeStr = `${dateStr}T${timeStr}`;
          const date = new Date(dateTimeStr);
          if (!isNaN(date.getTime())) {
            return date;
          }
        } else {
          const date = new Date(match.matchDate);
          if (!isNaN(date.getTime())) {
            return date;
          }
        }
      }

    } catch (error) {
      console.error("Error parsing date for match:", match.homeTeam?.name, "vs", match.awayTeam?.name, error);
    }
    return new Date("2099-12-31"); // Far future for invalid dates
  };

  // Calculate stats and filtered data on client side
  const { filteredMatches, stats, competitions, tournaments, totalPages } =
    useMemo(() => {
      // Extract unique competitions and tournaments
      const uniqueComps = [
        ...new Set(allMatches.map((m) => m.competition?.name).filter(Boolean)),
      ];
      const uniqueTourns = [
        ...new Set(allMatches.map((m) => m.tournament?.name).filter(Boolean)),
      ];

      // Apply filters
      let filtered = allMatches;

      if (selectedCompetition) {
        filtered = filtered.filter((match) =>
          match.competition?.name
            ?.toLowerCase()
            .includes(selectedCompetition.toLowerCase())
        );
      }

      if (selectedTournament) {
        filtered = filtered.filter((match) =>
          match.tournament?.name
            ?.toLowerCase()
            .includes(selectedTournament.toLowerCase())
        );
      }

      if (selectedStatus) {
        filtered = filtered.filter((match) => {
          const status = match.status || "";
          if (selectedStatus === "upcoming") {
            return status === "" || status === "upcoming";
          }
          return status === selectedStatus;
        });
      }

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (match) =>
            match.homeTeam?.name?.toLowerCase().includes(searchLower) ||
            match.awayTeam?.name?.toLowerCase().includes(searchLower) ||
            match.competition?.name?.toLowerCase().includes(searchLower) ||
            match.venue?.toLowerCase().includes(searchLower) ||
            match.tournament?.name?.toLowerCase().includes(searchLower)
        );
      }

      // Sort matches - truly upcoming matches first, then past matches
      filtered.sort((a, b) => {
        const statusA = (a.status || "").toLowerCase();
        const statusB = (b.status || "").toLowerCase();
        const dateA = getMatchDateTime(a);
        const dateB = getMatchDateTime(b);
        const now = new Date();

        // Check if matches are truly in the future
        const isFutureA = dateA > now;
        const isFutureB = dateB > now;

        // Check if matches are completed
        const isCompletedA = statusA === "completed" || statusA === "result" || statusA === "played";
        const isCompletedB = statusB === "completed" || statusB === "result" || statusB === "played";

        // Check if matches are fixtures but in the past (like yesterday's unplayed match)
        const isFixtureA = statusA === "fixture" || statusA === "upcoming" || !statusA || statusA === "";
        const isFixtureB = statusB === "fixture" || statusB === "upcoming" || !statusB || statusB === "";
        const isPastFixtureA = isFixtureA && !isFutureA;
        const isPastFixtureB = isFixtureB && !isFutureB;

        // Sort order:
        // 1. Future matches first (sorted by date ascending - soonest first)
        // 2. Past fixture matches next (sorted by date descending - most recent first)
        // 3. Completed matches last (sorted by date descending - most recent first)

        // Future matches always come first
        if (isFutureA && !isFutureB) return -1;
        if (!isFutureA && isFutureB) return 1;

        // Both future - show soonest first
        if (isFutureA && isFutureB) {
          return dateA - dateB;
        }

        // Past fixtures come before completed
        if (isPastFixtureA && isCompletedB) return -1;
        if (isCompletedA && isPastFixtureB) return 1;

        // Both past fixtures or both completed - show most recent first
        if ((isPastFixtureA && isPastFixtureB) || (isCompletedA && isCompletedB)) {
          return dateB - dateA;
        }

        // Default: sort by date
        return dateA - dateB;
      });

      // Calculate stats from all matches
      const totalMatches = allMatches.length;
      const now = new Date();

      // Count matches that are actually in the future (regardless of status)
      const upcomingMatches = allMatches.filter((match) => {
        const matchDate = getMatchDateTime(match);
        const status = (match.status || "").toLowerCase();
        const isFixture = status === "fixture" || status === "upcoming" || !status || status === "";
        // Count as upcoming if it's in the future OR if it's a fixture status (not completed)
        return matchDate > now && isFixture;
      }).length;

      const completedMatches = allMatches.filter(
        (match) => {
          // Check both status and matchStatus fields for compatibility
          const status = (match.status || "").toLowerCase();
          const matchStatus = (match.matchStatus || "").toLowerCase();
          return status === "played" || matchStatus === "played";
        }
      ).length;

      const liveMatches = allMatches.filter(
        (match) => {
          const status = (match.status || "").toLowerCase();
          return status === "live" || status === "in_progress";
        }
      ).length;

      // Pagination
      const totalPagesCount = Math.ceil(filtered.length / itemsPerPage);

      return {
        filteredMatches: filtered,
        stats: {
          totalMatches,
          totalCompetitions: uniqueComps.length,
          totalTournaments: uniqueTourns.length,
          liveMatches,
          completedMatches,
          upcomingMatches,
        },
        competitions: uniqueComps,
        tournaments: uniqueTourns,
        totalPages: totalPagesCount,
      };
    }, [
      allMatches,
      selectedCompetition,
      selectedTournament,
      selectedStatus,
      searchTerm,
    ]);

  // Get current page matches
  const currentMatches = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredMatches.slice(startIndex, endIndex);
  }, [filteredMatches, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompetition, selectedTournament, selectedStatus]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCompetition("");
    setSelectedTournament("");
    setSelectedStatus("");
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getStatusColor = (status) => {
    const statusLower = (status || "").toLowerCase();

    if (statusLower === "live") {
      return "#dc3545"; // Red for live
    }
    if (statusLower === "fixture" || statusLower === "upcoming" || !status || status === "") {
      return "#007bff"; // Blue for fixture/upcoming
    }
    if (statusLower === "completed" || statusLower === "result" || statusLower === "played") {
      return "#28a745"; // Green for completed
    }
    if (statusLower === "cancelled") {
      return "#6c757d"; // Gray for cancelled
    }
    return "#007bff";
  };

  const getStatusText = (status, match) => {
    const statusLower = (status || "").toLowerCase();

    // Check if match is live based on time
    const countdown = getTimeRemaining(match);
    if (countdown === "LIVE NOW") {
      return "LIVE";
    }

    if (statusLower === "fixture" || statusLower === "upcoming" || !status || status === "") {
      return "FIXTURE";
    }
    if (statusLower === "result" || statusLower === "completed" || statusLower === "played") {
      return "PLAYED";
    }
    if (statusLower === "live") {
      return "LIVE";
    }
    return status.toUpperCase();
  };

  const getTimeRemaining = (match) => {
    const matchDate = getMatchDateTime(match);

    // Check if it's an invalid date
    if (matchDate.getTime() === new Date("2099-12-31").getTime()) {
      return null;
    }

    // Get actual current time (real world time)
    const realNow = new Date();

    // Calculate time difference from real now to match date
    const diffMs = matchDate - realNow;

    // Check match status
    const statusLower = (match.status || "").toLowerCase();
    const isFixture = statusLower === "fixture" || statusLower === "upcoming" || !match.status || match.status === "";

    // For FIXTURE status matches, always show countdown
    if (isFixture) {
      // Check if match year is actually in the future
      const matchYear = matchDate.getFullYear();
      const currentYear = realNow.getFullYear();

      // If match is clearly in future year (2025 vs 2024), force positive countdown
      if (matchYear > currentYear) {
        const days = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60 * 24));
        const hours = Math.floor((Math.abs(diffMs) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((Math.abs(diffMs) % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((Math.abs(diffMs) % (1000 * 60)) / 1000);

        if (days > 365) {
          const years = Math.floor(days / 365);
          const remainingDays = days % 365;
          return `${years}y ${remainingDays}d`;
        } else if (days > 30) {
          return `${days} days`;
        } else if (days > 0) {
          return `${days}d ${hours}h ${minutes}m ${seconds}s`;
        } else if (hours > 0) {
          return `${hours}h ${minutes}m ${seconds}s`;
        } else if (minutes > 0) {
          return `${minutes}m ${seconds}s`;
        } else {
          return `${seconds}s`;
        }
      }

      // For same year or if diffMs is positive
      if (diffMs > 0) {
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

        if (days > 365) {
          const years = Math.floor(days / 365);
          const remainingDays = days % 365;
          return `${years}y ${remainingDays}d`;
        } else if (days > 30) {
          return `${days} days`;
        } else if (days > 0) {
          return `${days}d ${hours}h ${minutes}m ${seconds}s`;
        } else if (hours > 0) {
          return `${hours}h ${minutes}m ${seconds}s`;
        } else if (minutes > 0) {
          return `${minutes}m ${seconds}s`;
        } else {
          return `${seconds}s`;
        }
      } else {
        // Match date is in the past but still showing as fixture
        const absMs = Math.abs(diffMs);
        const matchDurationMs = 120 * 60 * 1000; // 2 hours

        if (absMs < matchDurationMs) {
          return "LIVE NOW";
        }

        // Show as recently finished
        const days = Math.floor(absMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((absMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) {
          return `Started ${days}d ${hours}h ago`;
        } else if (hours > 0) {
          return `Started ${hours}h ${minutes}m ago`;
        } else {
          return `Started ${minutes}m ago`;
        }
      }
    }

    // For non-fixture matches, only show countdown if in future
    if (diffMs <= 0) {
      return null;
    }

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    if (days > 30) {
      return `${days} days`;
    } else if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const toggleVenueExpand = (matchId) => {
    setExpandedVenues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(matchId)) {
        newSet.delete(matchId);
      } else {
        newSet.add(matchId);
      }
      return newSet;
    });
  };

  const getVenueDisplay = (match) => {
    let fullVenue = "TBD";

    // Check for detailed venue info first
    if (match.venueInfo?.longName) {
      fullVenue = match.venueInfo.longName;
    } else if (match.venueInfo?.shortName) {
      fullVenue = match.venueInfo.shortName;
    } else if (typeof match.venue === 'string' && match.venue) {
      fullVenue = match.venue;
    } else if (match.venue?.longName) {
      fullVenue = match.venue.longName;
    } else if (match.venue?.shortName) {
      fullVenue = match.venue.shortName;
    }

    const isExpanded = expandedVenues.has(match.matchId || match._id);
    const maxLength = 20;

    if (fullVenue.length <= maxLength || isExpanded) {
      return { text: fullVenue, showToggle: fullVenue.length > maxLength };
    }

    return {
      text: fullVenue.substring(0, maxLength) + '...',
      showToggle: true
    };
  };

  const formatDate = (dateString, match) => {
    try {
      let date;
      if (match && match.dateTime) {
        date = new Date(match.dateTime);
      } else if (dateString) {
        // Handle "2025-08-08Z" format
        const cleanDate = dateString.replace("Z", "");
        date = new Date(cleanDate);
        if (isNaN(date.getTime()) && match && match.date && match.time) {
          date = new Date(`${match.date}T${match.time}`);
        }
      }

      if (!date || isNaN(date.getTime())) {
        return "TBD";
      }

      // Format in English locale with Turkish timezone
      const formatted = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "Europe/Istanbul"
      });

      return formatted;
    } catch (error) {
      return "TBD";
    }
  };

  const formatTime = (dateString, match) => {
    try {
      // Use localTime if available (already in Turkish time)
      if (match && match.localTime) {
        const timeStr = match.localTime.replace("Z", "");
        const [hours, minutes] = timeStr.split(":");
        return `${hours}:${minutes}`;
      }

      // Otherwise parse and convert to Turkish time
      let date;
      if (match && match.time) {
        // Combine date and time for proper timezone conversion
        const dateStr = (match.date || match.matchDate || "").replace("Z", "");
        const timeStr = match.time.replace("Z", "");
        date = new Date(`${dateStr}T${timeStr}Z`); // Add Z to treat as UTC
      } else if (match && match.dateTime) {
        date = new Date(match.dateTime);
      } else if (dateString) {
        const cleanDate = dateString.replace("Z", "");
        date = new Date(cleanDate);
      }

      if (!date || isNaN(date.getTime())) {
        return "";
      }

      // Format in English with Turkish timezone
      const formatted = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Europe/Istanbul"
      });

      return formatted;
    } catch (error) {
      return match.localTime || match.time || "";
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 7;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className={styles.paginationBtn}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="start-ellipsis" className={styles.paginationEllipsis}>
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`${styles.paginationBtn} ${currentPage === i ? styles.active : ""}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="end-ellipsis" className={styles.paginationEllipsis}>
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className={styles.paginationBtn}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className={styles.tournamentScheduleContainer}>
        <div className={styles.tournamentScheduleContent}>
          <div className={styles.errorMessage}>
            <span className="material-icons">error</span>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tournamentScheduleContainer}>
      <div className={styles.tournamentScheduleContent}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Tournament Schedule</h1>
          <p className={styles.pageSubtitle}>
            Showing {filteredMatches.length} of {stats.totalMatches} matches •
            Turkish Super League 2025/2026
          </p>
        </div>

        {/* Statistics Section */}
        <div className={styles.statsSection}>
          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className={`${styles.statIcon} ${styles.fixtures}`}>
              <span className="material-icons">event</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.totalMatches}</div>
              <div className={styles.statLabel}>Total Matches</div>
            </div>
          </motion.div>

          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className={`${styles.statIcon} ${styles.competitions}`}>
              <span className="material-icons">emoji_events</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.totalCompetitions}</div>
              <div className={styles.statLabel}>Competitions</div>
            </div>
          </motion.div>

          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <div className={`${styles.statIcon} ${styles.completed}`}>
              <span className="material-icons">check_circle</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.completedMatches}</div>
              <div className={styles.statLabel}>Completed</div>
            </div>
          </motion.div>

          <motion.div
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <div className={`${styles.statIcon} ${styles.upcoming}`}>
              <span className="material-icons">schedule</span>
            </div>
            <div className={styles.statContent}>
              <div className={styles.statNumber}>{stats.upcomingMatches}</div>
              <div className={styles.statLabel}>Upcoming</div>
            </div>
          </motion.div>
        </div>

        {/* Compact Filters Section */}
        <div className={styles.compactFiltersSection}>
          <div className={styles.filtersRow}>
            {/* Search Input */}
            <div className={styles.compactSearch}>
              <div className={styles.searchInputWrapper}>
                <span className={`material-icons ${styles.searchIcon}`}>search</span>
                <input
                  type="text"
                  placeholder="Search teams, competition, venue..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className={styles.compactSearchInput}
                />
              </div>
            </div>

            {/* Filter Dropdowns */}
            <div className={styles.compactFilters}>
              <div className={styles.filterDropdown}>
                <select
                  value={selectedCompetition}
                  onChange={(e) => {
                    setSelectedCompetition(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.compactSelect}
                >
                  <option value="">All Competitions</option>
                  {competitions.map((comp) => (
                    <option key={comp} value={comp}>
                      {comp}
                    </option>
                  ))}
                </select>
                <span className={`material-icons ${styles.selectArrow}`}>expand_more</span>
              </div>

              <div className={styles.filterDropdown}>
                <select
                  value={selectedTournament}
                  onChange={(e) => {
                    setSelectedTournament(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.compactSelect}
                >
                  <option value="">All Tournaments</option>
                  {tournaments.map((tour) => (
                    <option key={tour} value={tour}>
                      {tour}
                    </option>
                  ))}
                </select>
                <span className={`material-icons ${styles.selectArrow}`}>expand_more</span>
              </div>

              <div className={styles.filterDropdown}>
                <select
                  value={selectedStatus}
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.compactSelect}
                >
                  <option value="">All Status</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="live">Live</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <span className={`material-icons ${styles.selectArrow}`}>expand_more</span>
              </div>

              <button
                onClick={clearFilters}
                className={styles.compactClearBtn}
                title="Clear Filters"
              >
                <span className="material-icons">clear</span>
              </button>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className={styles.tableContainer}>
          <table className={styles.scheduleTable}>
            <thead>
              <tr>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>
                    sports_soccer
                  </span>
                  Match
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>
                    emoji_events
                  </span>
                  Competition
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>today</span>
                  Date & Time
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>info</span>
                  Status
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>stadium</span>
                  Venue
                </th>
                <th>
                  <span className={`material-icons ${styles.tableIcon}`}>
                    format_list_numbered
                  </span>
                  Round
                </th>
              </tr>
            </thead>
            <tbody>
              {currentMatches?.map((match, index) => (
                <motion.tr
                  key={match.matchId || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="table-row"
                >
                  <td className={styles.matchCell}>
                    <div className={styles.matchInfo}>
                      <div className={styles.teamsContainer}>
                        <div className={styles.team}>
                          <div className={styles.teamLogo}>
                            {match.homeTeam?.teamLogoUrl ? (
                              <img
                                src={match.homeTeam.teamLogoUrl}
                                alt={match.homeTeam.name}
                                className={styles.teamImage}
                              />
                            ) : (
                              <span className="material-icons">shield</span>
                            )}
                          </div>
                          <div className={styles.teamInfo}>
                            <span className={styles.teamName}>
                              {match.homeTeam?.name || "TBD"}
                            </span>
                          </div>
                        </div>
                        <div className={styles.vsSeparator}>VS</div>
                        <div className={styles.team}>
                          <div className={styles.teamLogo}>
                            {match.awayTeam?.teamLogoUrl ? (
                              <img
                                src={match.awayTeam.teamLogoUrl}
                                alt={match.awayTeam.name}
                                className={styles.teamImage}
                              />
                            ) : (
                              <span className="material-icons">shield</span>
                            )}
                          </div>
                          <div className={styles.teamInfo}>
                            <span className={styles.teamName}>
                              {match.awayTeam?.name || "TBD"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.competitionBadge}>
                      {(() => {
                        const competitionName =
                          match.competition?.name || "N/A";
                        return competitionName;
                      })()}
                    </div>
                  </td>
                  <td>
                    <div className={styles.dateInfo}>
                      <span className={styles.matchDate}>
                        {formatDate(match.date || match.matchDate, match)}
                      </span>
                      {formatTime(match.date || match.matchDate, match) && (
                        <span className={styles.matchTime}>
                          {formatTime(match.date || match.matchDate, match)}
                        </span>
                      )}
                      {/* Show countdown for all future matches */}
                      {(() => {
                        const countdown = getTimeRemaining(match);

                        if (countdown) {
                          const isLive = countdown === "LIVE NOW";
                          const isPast = countdown.includes("ago");
                          return (
                            <span className={`${styles.countdown} ${isLive ? styles.liveNow : ''} ${isPast ? styles.pastMatch : ''}`}>
                              <span className="material-icons" style={{fontSize: '14px', marginRight: '4px'}}>
                                {isLive ? 'radio_button_checked' : isPast ? 'history' : 'timer'}
                              </span>
                              {countdown}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </td>
                  <td>
                    <div
                      className={styles.statusBadge}
                      style={{
                        backgroundColor: getStatusColor(match.status),
                      }}
                    >
                      {getStatusText(match.status, match)}
                    </div>
                  </td>
                  <td>
                    <div className={styles.venueCell}>
                      <span className={styles.venueText}>
                        {getVenueDisplay(match).text}
                      </span>
                      {getVenueDisplay(match).showToggle && (
                        <button
                          className={styles.venueToggle}
                          onClick={() => toggleVenueExpand(match.matchId || match._id)}
                        >
                          {expandedVenues.has(match.matchId || match._id) ? 'Less' : 'View All'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={styles.roundText}>
                      {match.round || match.week || "-"}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>

          {currentMatches?.length === 0 && (
            <div className={styles.emptyState}>
              <span className={`material-icons ${styles.emptyIcon}`}>search_off</span>
              <h3>No matches found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.paginationContainer}>
            <div className={styles.paginationControls}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`${styles.paginationBtn} ${styles.navBtn}`}
              >
                <span className="material-icons">chevron_left</span>
              </button>
              {renderPagination()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`${styles.paginationBtn} ${styles.navBtn}`}
              >
                <span className="material-icons">chevron_right</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentSchedule;
